import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/firebase';
import {
  PublishTenantSiteResponse,
  PublishValidationResult,
  SiteDraft,
  TenantPublishStatus,
  TenantPublishingError,
  TenantSiteDraft,
  UpdateTenantSiteDraftResponse,
  ProvisionTenantInput,
  ProvisionTenantResponse,
  ActivateTenantResponse,
  TenantOnboardingStatus,
  OnboardingStepId,
  SaveMyOnboardingAvailabilityInput,
  SaveMyOnboardingModeInput,
  ConfirmMyOnboardingPlanInput,
  SiteExperience,
  UpdateTenantResourceTerminologyInput,
  UpdateTenantResourceTerminologyResponse,
} from './types';
import { normalizePublishingError } from './errors';

const assertTenantId = (tenantId: string): string => {
  if (!/^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/.test(tenantId)) {
    throw new TenantPublishingError('INVALID_ARGUMENT', 'The active tenant is invalid.');
  }
  return tenantId;
};

const call = async <TRequest extends Record<string, unknown>, TResponse>(
  name: string,
  request: TRequest
): Promise<TResponse> => {
  try {
    const result = await httpsCallable<TRequest, TResponse>(functions, name)(request);
    return result.data;
  } catch (error) {
    throw normalizePublishingError(error);
  }
};

export const createPublishingIdempotencyKey = (): string => {
  const randomPart = typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
  return `publish-${randomPart}`.replace(/[^A-Za-z0-9_-]/g, '-');
};

export const getTenantSiteDraft = (tenantId: string): Promise<TenantSiteDraft> =>
  call('getTenantSiteDraft', { tenantId: assertTenantId(tenantId) });

export const updateTenantSiteDraft = (
  tenantId: string,
  input: { expectedDraftVersion: number; schemaVersion: number; content: SiteDraft }
): Promise<UpdateTenantSiteDraftResponse> => call('updateTenantSiteDraft', {
  tenantId: assertTenantId(tenantId),
  ...input,
});

export const validateTenantPublish = (
  tenantId: string,
  expectedDraftVersion: number
): Promise<PublishValidationResult> => call('validateTenantPublish', {
  tenantId: assertTenantId(tenantId),
  expectedDraftVersion,
});

export const publishTenantSite = (
  tenantId: string,
  input: { expectedDraftVersion: number; idempotencyKey: string; confirmOnboardingSetup?: boolean }
): Promise<PublishTenantSiteResponse> => call('publishTenantSite', {
  tenantId: assertTenantId(tenantId),
  ...input,
});

export const getTenantPublishStatus = (tenantId: string): Promise<TenantPublishStatus> =>
  call('getTenantPublishStatus', { tenantId: assertTenantId(tenantId) });

export const provisionTenant = (input: ProvisionTenantInput): Promise<ProvisionTenantResponse> =>
  call('provisionTenant', { ...input, tenantId: assertTenantId(input.tenantId) });

export const updateTenantResourceTerminology = (
  input: UpdateTenantResourceTerminologyInput
): Promise<UpdateTenantResourceTerminologyResponse> => call('updateTenantResourceTerminology', {
  ...input,
  tenantId: assertTenantId(input.tenantId),
});

export const activateProvisionedTenant = (tenantId: string): Promise<ActivateTenantResponse> =>
  call('activateProvisionedTenant', { tenantId: assertTenantId(tenantId) });

// Super admin: turn preview mode on or off for a business no owner has signed up for.
export const setTenantSalesPreview = (tenantId: string, enabled: boolean): Promise<{
  tenantId: string;
  salesPreview: boolean;
  publicUrl?: string;
  preview?: { published: boolean; issues: string[] };
}> => call('setTenantSalesPreview', { tenantId: assertTenantId(tenantId), enabled });

// Emails the owner a fresh 7-day invitation. Pass owner to set or change who it
// goes to (needed for businesses built before the owner was known).
export const reissueProvisionedTenantInvitation = (
  tenantId: string,
  owner?: { ownerName: string; ownerEmail: string }
): Promise<{
  tenantId: string; ownerEmail: string; accessCode: string; expiresAt: string;
}> => call('reissueProvisionedTenantInvitation', { tenantId: assertTenantId(tenantId), ...(owner ?? {}) });

export const getMyTenantOnboardingStatus = (tenantId: string): Promise<TenantOnboardingStatus> =>
  call('getMyTenantOnboardingStatus', { tenantId: assertTenantId(tenantId) });

export const completeMyOnboardingIntroduction = (tenantId: string): Promise<TenantOnboardingStatus> =>
  call('completeMyOnboardingIntroduction', { tenantId: assertTenantId(tenantId) });

export const updateMyOnboardingBusinessDetails = (
  tenantId: string,
  input: { displayName: string; timezone: string; businessType?: string }
): Promise<TenantOnboardingStatus> => call('updateMyOnboardingBusinessDetails', {
  tenantId: assertTenantId(tenantId), ...input,
});

export const adoptMyPreparedServices = (tenantId: string): Promise<TenantOnboardingStatus> =>
  call('adoptMyPreparedServices', { tenantId });

export const completeMyOnboardingService = (
  tenantId: string,
  input: { serviceId: string; category?: string }
): Promise<TenantOnboardingStatus> => call('completeMyOnboardingService', {
  tenantId: assertTenantId(tenantId), ...input,
});

export const completeMyOnboardingSiteIntroduction = (tenantId: string): Promise<TenantOnboardingStatus> =>
  call('completeMyOnboardingSiteIntroduction', { tenantId: assertTenantId(tenantId) });

export const saveMyOnboardingMode = (
  tenantId: string,
  input: SaveMyOnboardingModeInput
): Promise<TenantOnboardingStatus> => call('saveMyOnboardingMode', {
  tenantId: assertTenantId(tenantId), ...input,
});

export const confirmMyOnboardingPlan = (
  tenantId: string,
  input: ConfirmMyOnboardingPlanInput
): Promise<TenantOnboardingStatus> => call('confirmMyOnboardingPlan', {
  tenantId: assertTenantId(tenantId), ...input,
});

export const startMyOnboardingStep = (
  tenantId: string,
  stepId: OnboardingStepId
): Promise<TenantOnboardingStatus> => call('startMyOnboardingStep', {
  tenantId: assertTenantId(tenantId), stepId,
});

export const saveMyOnboardingAvailability = (
  tenantId: string,
  input: SaveMyOnboardingAvailabilityInput
): Promise<TenantOnboardingStatus> => call('saveMyOnboardingAvailability', {
  tenantId: assertTenantId(tenantId), ...input,
});

export const chooseMyOnboardingSiteExperience = async (
  tenantId: string,
  experience: SiteExperience
): Promise<TenantOnboardingStatus> => {
  const status = await call<{ tenantId: string; experience: SiteExperience }, TenantOnboardingStatus>(
    'chooseMyOnboardingSiteExperience', { tenantId: assertTenantId(tenantId), experience }
  );
  if (experience !== 'booking_only' || status.gates.sitePublished.complete) return status;
  const draft = await getTenantSiteDraft(tenantId);
  await publishTenantSite(tenantId, {
    expectedDraftVersion: draft.draftVersion,
    idempotencyKey: createPublishingIdempotencyKey(),
    confirmOnboardingSetup: true,
  });
  return getMyTenantOnboardingStatus(tenantId);
};

export const confirmMyTenantSettings = (tenantId: string): Promise<TenantOnboardingStatus> =>
  call('confirmMyTenantSettings', { tenantId: assertTenantId(tenantId) });

export const setMyTenantLocalSubscription = (tenantId: string): Promise<TenantOnboardingStatus | {
  tenantId: string; subscriptionStatus: 'trialing';
}> =>
  call('setMyTenantLocalSubscription', { tenantId: assertTenantId(tenantId) });

export const activateMyTenant = (tenantId: string): Promise<ActivateTenantResponse> =>
  call('activateMyTenant', {
    tenantId: assertTenantId(tenantId),
    acceptedPolicy: true,
    acceptedPolicyVersion: 'marketing-policies-2026-09-07',
  });

export { normalizePublishingError } from './errors';

export type TenantAccess =
  | { tenantId: string | null; closed: false }
  | { tenantId: string; closed: true; purgeAfter: string };

export const getMyTenantAccess = (): Promise<TenantAccess> => call('getMyTenantAccess', {});

export const closeTenant = (tenantId: string, confirmation: string): Promise<{
  tenantId: string; purgeAfter: string; stripe: 'canceled' | 'none' | 'skipped' | 'failed';
}> => call('closeTenant', { tenantId: assertTenantId(tenantId), confirmation });

export const restoreTenant = (tenantId: string): Promise<{ tenantId: string; lifecycleState: string }> =>
  call('restoreTenant', { tenantId: assertTenantId(tenantId) });

export const purgeClosedTenantNow = (tenantId: string): Promise<{ tenantId: string; deletedLogins: number }> =>
  call('purgeClosedTenantNow', { tenantId: assertTenantId(tenantId) });
